import { motion } from 'framer-motion';
import { Component } from 'react';
import { cardUnits, playsContainingAll } from '../../../functions';
import { ISourcedCard, PlayerHand } from '../../../Models/CardModels';
import { IChatMessage } from '../../../Models/LobbyModels';
import { IVisibleGameState, IVisiblePlay } from '../../../Models/StateModels';
import Opponents from '../../Opponent/Opponents';
import { STACK_OFFSET } from '../../Player/HandCards/HandCards';
import Player from '../../Player/Player';
import Dock from '../Dock/Dock';
import FeltMat, { PLAY_AREA_ID } from '../FeltMat/FeltMat';
import './GameState.css';
import {
  DndContext,
  DragOverlay,
  DragEndEvent,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import Card from '../Card/Card';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

const DRAG_SENSORS = [
  { sensor: PointerSensor, options: { activationConstraint: { distance: 6 } } },
  { sensor: KeyboardSensor, options: {} },
];

const DROP_ANIMATION: DropAnimation = {
  keyframes: ({ active, transform: { initial, final } }) => [
    { transform: `${CSS.Transform.toString(initial)} rotate(0deg)` },
    {
      transform: `${CSS.Transform.toString(final)} rotate(${active.data.current?.rotation ?? 0}deg)`,
    },
  ],
};

interface IGameStateProps {
  visibleGameState: IVisibleGameState;
  playerId: number;
  playHistory: IVisiblePlay[];
  tryPlay: (play: ISourcedCard[]) => Promise<void>;
  trySwap: (card1: ISourcedCard, card2: ISourcedCard) => Promise<void>;
  tryPickup: () => void;
  handleError: (e: Error) => void;

  sendMessage: (message: string) => void;

  messages: IChatMessage[];
}

interface IGameStateState {
  draggingCardId: string | undefined;
  draggingCards: ISourcedCard[];
  draggingCardClassName: string | undefined;
  draggingCardRotation: number;
  selectedCardIds: string[];
  rejectedCardIds: string[];
  hasSwapped: boolean;
  pendingPlay: ISourcedCard[] | undefined;
}

const REJECT_DURATION_MS = 400;

class GameState extends Component<IGameStateProps, IGameStateState> {
  state: IGameStateState = {
    draggingCardId: undefined,
    draggingCards: [],
    draggingCardClassName: undefined,
    draggingCardRotation: 0,
    selectedCardIds: [],
    rejectedCardIds: [],
    hasSwapped: false,
    pendingPlay: undefined,
  };

  private rejectTimeout: ReturnType<typeof setTimeout> | undefined;

  public componentDidUpdate(prevProps: IGameStateProps) {
    const { player } = this.props.visibleGameState;
    const { selectedCardIds } = this.state;
    const isSwapPhaseStarting =
      player.canSwap && !prevProps.visibleGameState.player.canSwap;
    if (isSwapPhaseStarting) {
      this.setState({ hasSwapped: false });
    }
    if (this.state.pendingPlay && !player.isTurn) {
      this.setState({ pendingPlay: undefined });
    }
    const isTurnStarting =
      player.isTurn && !prevProps.visibleGameState.player.isTurn;
    if (
      isTurnStarting &&
      selectedCardIds.length > 0 &&
      playsContainingAll(player.availablePlays, selectedCardIds).length === 0
    ) {
      this.setSelectedCardIds([]);
      this.reject(selectedCardIds);
    }
  }

  public componentWillUnmount() {
    clearTimeout(this.rejectTimeout);
  }

  public render() {
    const { handleError } = this.props;
    const { opponents, playedCards, player } = this.props.visibleGameState;
    const { playHistory } = this.props;
    const displayedOpponents = opponents.map((opponent) => ({
      opponent,
      lastSentMessage: this.props.messages
        .filter((x) => x.playerIdHash === opponent.idHash)
        .pop(),
    }));
    const lastPlayerMessage = this.props.messages
      .filter((x) => x.playerIdHash === player.idHash)
      .pop();

    return (
      <DndContext
        sensors={DRAG_SENSORS}
        onDragStart={this.handleDragStart}
        onDragEnd={this.handleDragEnd}
      >
        <div className="game-state-wrapper">
          <div className="game-state">
            <Opponents player={player} opponents={displayedOpponents} />
            <FeltMat
              visibleGameState={this.props.visibleGameState}
              playHistory={playHistory}
              pickup={this.pickup}
            />
            <Player
              player={player}
              handleError={handleError}
              selectedCardIds={this.state.selectedCardIds}
              rejectedCardIds={this.state.rejectedCardIds}
              onSelectionChange={this.setSelectedCardIds}
              onReject={this.reject}
            />
            <Dock
              player={player}
              opponents={opponents}
              pileCount={playedCards.length}
              lastPlayerMessage={lastPlayerMessage}
              sendMessage={this.props.sendMessage}
              pickup={this.pickup}
            />
          </div>
        </div>

        <DragOverlay dropAnimation={DROP_ANIMATION}>
          {this.state.draggingCards.length > 0 ? (
            <motion.div
              className="relative h-full w-full"
              initial={{ rotate: this.state.draggingCardRotation }}
              animate={{ rotate: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              {this.renderDraggingCards()}
            </motion.div>
          ) : null}
        </DragOverlay>

        {this.state.pendingPlay && (
          <ConfirmDialog
            title="Play without swapping?"
            message="You haven't swapped any cards yet. Once you play, you can't swap any more."
            confirmLabel="Play anyway"
            cancelLabel="Keep swapping"
            onConfirm={this.confirmPendingPlay}
            onCancel={this.cancelPendingPlay}
          />
        )}
      </DndContext>
    );
  }

  private renderDraggingCards() {
    const { draggingCards, draggingCardId, draggingCardClassName } = this.state;
    const grabbedIndex = draggingCards.findIndex(
      (c) => c.card.cardId === draggingCardId
    );
    return draggingCards.map((c, i) => (
      <div
        key={c.card.cardId}
        className="absolute top-0"
        style={{ left: cardUnits((i - grabbedIndex) * STACK_OFFSET) }}
      >
        <Card card={c.card} className={draggingCardClassName} />
      </div>
    ));
  }

  private setSelectedCardIds = (selectedCardIds: string[]) => {
    this.setState({ selectedCardIds });
  };

  private reject = (rejectedCardIds: string[]) => {
    clearTimeout(this.rejectTimeout);
    this.setState({ rejectedCardIds: [] }, () =>
      requestAnimationFrame(() => {
        this.setState({ rejectedCardIds });
        this.rejectTimeout = setTimeout(
          () => this.setState({ rejectedCardIds: [] }),
          REJECT_DURATION_MS
        );
      })
    );
  };

  private play = (cards: ISourcedCard[]) => {
    if (this.props.visibleGameState.player.canSwap && !this.state.hasSwapped) {
      this.setState({ pendingPlay: cards });
      return;
    }
    this.setSelectedCardIds([]);
    this.props.tryPlay(cards);
  };

  private confirmPendingPlay = () => {
    const { pendingPlay } = this.state;
    this.setState({ pendingPlay: undefined });
    if (pendingPlay) {
      this.setSelectedCardIds([]);
      this.props.tryPlay(pendingPlay);
    }
  };

  private cancelPendingPlay = () => {
    this.setState({ pendingPlay: undefined });
  };

  private swap = (card1: ISourcedCard, card2: ISourcedCard) => {
    this.setSelectedCardIds([]);
    this.setState({ hasSwapped: true });
    this.props.trySwap(card1, card2);
  };

  private pickup = () => {
    this.setSelectedCardIds([]);
    this.props.tryPickup();
  };

  private handleDragStart = (event: DragStartEvent) => {
    this.setState({
      draggingCardId: event.active.id as string,
      draggingCards: event.active.data.current?.dragGroup ?? [],
      draggingCardClassName: event.active.data.current?.cardClassName,
      draggingCardRotation: event.active.data.current?.rotation ?? 0,
    });
  };

  private handleDragEnd = (e: DragEndEvent) => {
    this.setState({
      draggingCardId: undefined,
      draggingCards: [],
      draggingCardClassName: undefined,
    });
    const { active, over } = e;
    const handCards = this.props.visibleGameState.player.handCards;
    const isHandCardDroppedOnHand =
      handCards.some((c) => c.cardId === active.id) &&
      handCards.some((c) => c.cardId === over?.id);
    if (isHandCardDroppedOnHand) {
      return;
    }

    const draggedCards: ISourcedCard[] = active.data.current?.dragGroup ?? [];
    const draggedCard = draggedCards.find((c) => c.card.cardId === active.id);

    // is over play zone
    if (
      over?.id === PLAY_AREA_ID &&
      draggedCard &&
      playsContainingAll(
        this.props.visibleGameState.player.availablePlays,
        draggedCards.map((c) => c.card.cardId)
      ).length > 0
    ) {
      this.play(draggedCards);
      return;
    }

    // is over another card
    if (
      this.props.visibleGameState.player.canSwap &&
      over &&
      draggedCard &&
      over.id !== draggedCard.card.cardId
    ) {
      const overCard = this.props.visibleGameState.player.hiddenCards
        .map((x) => ({ source: PlayerHand.HIDDEN, card: x }) as ISourcedCard)
        .concat(
          this.props.visibleGameState.player.handCards.map(
            (x) => ({ source: PlayerHand.HAND, card: x }) as ISourcedCard
          )
        )
        .concat(
          this.props.visibleGameState.player.visibleCards.map(
            (x) => ({ source: PlayerHand.VISIBLE, card: x }) as ISourcedCard
          )
        )
        .find((x) => x.card.cardId === over.id);
      if (overCard) {
        this.swap(draggedCard, overCard);
        return;
      }
    }
  };
}

export default GameState;
